import React, { FC } from 'react';
import { UserPatientProfile } from '@maiia/model/generated/model/api-patient/api-patient';
import { darken, lighten, styled, css } from '@mui/material/styles';
import {
  Avatar as MuiAvatar,
  AvatarProps as MuiAvatarProps,
  Box,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useTranslation } from '@/src/i18n';

const StyledPatientNameTag = styled(Box, {
  shouldForwardProp: propName => propName !== 'profileIndex',
})<{ profileIndex: number }>(({ theme, profileIndex }) => {
  const {
    spacing,
    palette: { primary, patients },
  } = theme;

  const avatarBgColor =
    patients[profileIndex > 8 ? profileIndex % 9 : profileIndex] ||
    primary.main;

  return css`
    text-transform: capitalize;
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-template-rows: 1fr min-content;
    align-items: center;
    column-gap: ${spacing(1.5)};
    grid-template-areas:
      'avatar info'
      'avatar default';

    ${theme.breakpoints.up('sm')} {
      grid-template-columns: min-content 1fr max-content;
      grid-template-rows: min-content;
      grid-template-areas: 'avatar info default';
    }

    &.vertical {
      grid-template-columns: 1fr;
      grid-template-rows: repeat(max-content, 3);
      justify-items: center;
      text-align: center;
      grid-template-areas:
        'avatar'
        'info'
        'default';

      .MuiAvatar-root {
        margin-bottom: ${spacing(1)};
      }
    }

    &.clickable {
      cursor: pointer;
    }

    .MuiAvatar-root {
      grid-area: avatar;
      background-color: ${avatarBgColor};
      transition: background-color 0.15s ease-out;
      text-shadow: 1px 1px 1px ${darken(avatarBgColor, 0.25)};
      box-shadow: inset 1px 1px 5px ${darken(avatarBgColor, 0.1)};
    }

    .patient-name {
      font-weight: 500;
      transition: color 0.15s ease-out;
    }

    .birth-date,
    .default-account {
      color: ${theme.palette.text.medium};
    }

    .default-account {
      font-size: ${spacing(1.5)};
      font-weight: 500;
    }

    &.clickable:hover {
      .MuiAvatar-root {
        background-color: ${lighten(avatarBgColor, 0.2)};
      }

      .patient-name {
        color: ${primary.main};
      }
    }
  `;
});

type PatientNameTagProps = {
  profile: UserPatientProfile;
  profileIndex?: number;
  nameVariant?: 'none' | 'first' | 'full';
  showDefault?: boolean;
  showBirthDate?: boolean;
  clickable?: boolean;
  selected?: boolean;
  vertical?: boolean;
  avatarSize?: AvatarSize;
  sx?: any;
} & React.HTMLAttributes<HTMLDivElement>;

const PatientNameTag: FC<PatientNameTagProps> = ({
  profile,
  profileIndex = 0,
  nameVariant = 'full',
  showDefault,
  showBirthDate,
  clickable,
  selected,
  vertical,
  avatarSize,
  sx = {},
  ...rest
}) => {
  const { t } = useTranslation();
  const { firstName, lastName, birthDate, isDefault } = profile;

  const patientName =
    nameVariant === 'first' ? firstName : `${firstName} ${lastName}`;
  const initials = firstName?.[0] || '!';

  return (
    <StyledPatientNameTag
      className={clsx(
        'patient-nametag',
        clickable && 'clickable',
        vertical && 'vertical',
        selected && 'selected',
      )}
      profileIndex={profileIndex}
      sx={sx}
      {...rest}
    >
      {/* Patient's avatar */}
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <Avatar size={avatarSize} selected={selected}>
        {initials}
      </Avatar>

      {/* Patient's name and birth date */}
      {(nameVariant !== 'none' || showBirthDate) && (
        <div className="info">
          {nameVariant !== 'none' && (
            <Typography className="patient-name" data-testid="patient-name">
              {patientName}
            </Typography>
          )}

          {showBirthDate && (
            <Typography className="birth-date" data-testid="birth-date">
              {dayjs(birthDate).format('DD/MM/YYYY')}
            </Typography>
          )}
        </div>
      )}

      {/* Default tag */}
      {showDefault && isDefault && (
        <Typography className="default-account" data-testid="default-account">
          {t('default-account')}
        </Typography>
      )}
    </StyledPatientNameTag>
  );
};

// =======================================================================
// Below, will be on its own file after refactoring practitioner's avatar
// =======================================================================

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const StyledAvatar = styled(MuiAvatar)(
  ({
    theme: {
      spacing,
      palette: { secondary, background },
    },
  }) => css`
    &.xs {
      /* 20px */
      width: ${spacing(3)};
      height: ${spacing(3)};
      font-size: ${spacing(2)};
    }

    &.sm {
      /* 32px */
      width: ${spacing(4)};
      height: ${spacing(4)};
    }

    &.md {
      /* 40px */
      width: ${spacing(5)};
      height: ${spacing(5)};
    }

    &.lg {
      /* 52px */
      width: ${spacing(6.5)};
      height: ${spacing(6.5)};
    }

    &.xl {
      /* 96px */
      width: ${spacing(12)};
      height: ${spacing(12)};
    }

    &.selected {
      border: ${spacing(0.4)} solid ${secondary.main};
      overflow: visible;

      /* Checkmark badge */
      &::after {
        content: '✓';
        font-size: ${spacing(1.5)};
        background-color: ${secondary.main};
        display: flex;
        flex-direction: column;
        justify-content: center;
        position: absolute;
        /* To comensate for borders */
        top: 85.355%; /* x = x0 + r*cos(a) where (x0,y0) are center coordinates, r the radius and a the angle */
        left: 85.355%; /* y = y0 + r*sin(a) where (x0,y0) are center coordinates, r the radius and a the angle */
        transform: translate(-50%, -50%);
        width: 50%;
        height: 50%;
        border: 2px solid ${background.secondary};
        border-radius: 50%;
        text-shadow: none;
      }
    }
  `,
);

type AvatarProps = {
  size?: AvatarSize;
  selected?: boolean;
} & MuiAvatarProps;

const Avatar: FC<AvatarProps> = ({
  size = 'md',
  selected,
  children,
  ...rest
}) => (
  <StyledAvatar className={clsx(size, selected && 'selected')} {...rest}>
    {children}
  </StyledAvatar>
);

export default PatientNameTag;
